import MarkdownIt from "markdown-it";

const markdown = new MarkdownIt({ html: true });
const missingImage = "題幹圖片尚未附圖或無法辨識；請補上圖片網址、儲存位置或 Python 圖形程式碼後再產生詳解。";

const validateUrl = (value) => {
  const url = String(value || "").trim();
  if (/^data:image\/[\w.+-]+;base64,[\s\S]+$/i.test(url)) return url;
  let parsed;
  try { parsed = new URL(url); } catch { throw new Error(missingImage); }
  if (!["https:", "http:"].includes(parsed.protocol) || parsed.username || parsed.password) {
    throw new Error("題幹圖片網址無法由 AI 讀取；請改用完整 HTTP(S) 圖片網址或重新附圖。");
  }
  return url;
};

export const collectQuestionPromptImages = ({ prompt = "", assets = [] } = {}) => {
  const collected = [];
  const seen = new Set();
  const seenUrls = new Set();
  const add = (asset) => {
    const { url: urlValue, storage_key: storageKey, source_code: code, source_language: language } = getQuestionPromptImageSource(asset);
    const urls = [asset.url, asset._preview_url, asset._previewUrl].map((url) => String(url || "").trim()).filter(Boolean);
    if (!code && !storageKey && urls.some((url) => seenUrls.has(url))) return;
    if (!urlValue && !storageKey && !code) throw new Error(missingImage);
    // Python source is rendered by the backend even when its saved URL is stale.
    const url = urlValue && !code && !storageKey ? validateUrl(urlValue) : urlValue;
    if (code && language !== "python") throw new Error("題幹圖片程式碼尚未支援；請先附上實際圖片再產生詳解。");
    const identity = JSON.stringify([url, storageKey, code]);
    if (seen.has(identity)) return;
    seen.add(identity);
    urls.forEach((url) => seenUrls.add(url));
    collected.push({
      ...asset, role: "prompt", url, storage_key: storageKey, source_code: code, source_language: language,
    });
  };
  const sourcePriority = (asset) => String(asset.source_code || "").trim() ? 2 : String(asset.storage_key || "").trim() ? 1 : 0;
  assets.filter((asset) => asset?.role === "prompt").sort((left, right) => sourcePriority(right) - sourcePriority(left)).forEach(add);

  const tokens = markdown.parse(String(prompt), {});
  let imageCount = 0;
  const visit = (items) => items.forEach((token) => {
    if (token.type === "image") {
      imageCount += 1;
      add({ url: token.attrGet("src"), alt_text: token.content || "題幹圖片" });
    }
    if (token.type === "html_inline" || token.type === "html_block") {
      const html = new DOMParser().parseFromString(token.content, "text/html");
      const images = [...html.querySelectorAll("img")];
      if (/<img\b/i.test(token.content) && !images.length) throw new Error(missingImage);
      images.forEach((image) => {
        if (!image.getAttribute("src") || image.hasAttribute("srcset")) throw new Error(missingImage);
        add({ url: image.getAttribute("src"), alt_text: image.getAttribute("alt") || "題幹圖片" });
      });
    }
    if (token.children) visit(token.children);
  });
  visit(tokens);
  const visibleText = String(prompt).replace(/(`{3,}|~{3,})[\s\S]*?\1/g, "").replace(/`[^`]*`/g, "");
  const imageMarkers = visibleText.match(/(?<!\\)!\[/g) || [];
  if (imageMarkers.length > imageCount || /\[附圖待處理(?:[（(]題目[）)])?[：:]/.test(visibleText)) throw new Error(missingImage);
  return collected;
};

export const getQuestionPromptImageSource = (asset = {}) => {
  const storage_key = String(asset.storage_key || "").trim();
  const source_code = String(asset.source_code || "").trim();
  const url = String(asset.url || "").trim() || (!storage_key && !source_code ? String(asset._preview_url || asset._previewUrl || "").trim() : "");
  const source_language = String(asset.source_language || (source_code ? "python" : "")).trim().toLowerCase();
  return { url, storage_key, source_code, source_language };
};
